import { Button, Heading, Modal, ModalContent, Spinner, Text } from 'telia-front-react';
import { observer } from 'mobx-react';
import React from 'react';
import UiNotice from '../components/UiNotice';
import { AccountReturnParams } from '../pages/AccountPage';
import PanelSelector from '../panels/PanelSelector';
import SelectedMobilePanel from '../panels/SelectedMobilePanel';
import { ACCOUNT } from '../Routes';
import useStores from '../stores/UseStores';
import { FetchStatus } from '../util/ApiUtil';
import { useIsMobileHook } from '../util/hooks/useIsMobile';
import { isSmallModuleView } from '../util/ModuleUtil';

interface PersonalCodeBindingProps {
  returnParams: AccountReturnParams;
}

const PersonalCodeBinding = observer((props: PersonalCodeBindingProps): React.JSX.Element => {
  const { returnParams } = props;
  const {
    translateStore: { translate },
    homeStore: { status: homeStoreStatus, loadIndexData, panels, selectedPanelInStorage },
    routeStore,
    accountInfoStore,
    uiStore: { hideNotices, selectedTab, componentWidth },
    mergeStore,
  } = useStores();
  const { isMobile } = useIsMobileHook();
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const shouldShowMobileNavigation = isMobile || isSmallModuleView(componentWidth);
  const [backToMethodSelectionClicked, setBackToMethodSelectionClicked] = React.useState<boolean>(false);
  let previousParams: string | undefined;

  React.useEffect(() => {
    mergeStore.resolvedMergeParams.confirmSkipped && onConfirmOrCancel();
  }, [mergeStore.resolvedMergeParams.confirmSkipped]);

  const getCurrentTab = (): string => {
    if (panels.length === 1) {
      return panels[0].id;
    }
    if (selectedTab) {
      return selectedTab;
    }
    if (!backToMethodSelectionClicked) {
      return routeStore.hash || selectedPanelInStorage || '';
    }
    return '';
  };

  const openModal = () => {
    previousParams = routeStore.params;
    routeStore.replace(
      `${ACCOUNT}${routeStore.formForwardParamsAnd({
        ...returnParams,
        authlevel: '2',
        ForceAuth: 'true',
      })}`,
    );
    hideNotices();
    loadIndexData();
    setModalOpen(true);
  };

  const closeModal = () => {
    routeStore.historyReplace(`${ACCOUNT}?`);
    routeStore.replace(`${ACCOUNT}${previousParams || ''}`);
    previousParams = undefined;
    setModalOpen(false);
  };

  const panelSelector = () => {
    switch (homeStoreStatus) {
      case FetchStatus.UNINITIALIZED:
      case FetchStatus.FETCHING:
        return <Spinner />;
      case FetchStatus.ERROR:
        return <UiNotice />;
      default:
        return <PanelSelector />;
    }
  };

  const resetAccountPage = async () => {
    await accountInfoStore.loadInitialInfo();
  };

  const onConfirmOrCancel = async () => {
    closeModal();
    resetAccountPage();
  };

  const currentTab = React.useMemo(getCurrentTab, [panels, selectedTab, backToMethodSelectionClicked, selectedPanelInStorage]);

  const loginModal = () => {
    if (shouldShowMobileNavigation && currentTab) {
      const selectedPanel = panels.find((panel) => panel.id === currentTab);

      if (selectedPanel) {
        return <SelectedMobilePanel panel={selectedPanel} setBackToMethodSelectionClicked={setBackToMethodSelectionClicked} />;
      }
    }
    return (
      <Text>
        <Heading level={1} className="h3 text-center" id="add-personal-code-modal-title">
          {translate('accountPage.merge.login.title')}
        </Heading>
        {panelSelector()}
      </Text>
    );
  };

  return (
    <>
      <Button onClick={() => openModal()} text={translate('accountPage.personalCodeBindingButton.label')} iconRight="#arrow-right" />
      {!mergeStore.canMergeAccounts() && (
        <Modal
          isOpen={modalOpen}
          isClosable={true}
          onClose={() => closeModal()}
          hasContentComponent={true}
          size="10-col"
          aria-labelledby="add-personal-code-modal-title"
        >
          <ModalContent>{loginModal()}</ModalContent>
        </Modal>
      )}
    </>
  );
});

export default PersonalCodeBinding;
